import React, { useState, useEffect } from "react";
import {
  Users,
  Phone,
  Building2,
  Loader,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const AccountDuplicatesSidebar = ({ accountId, accountName }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (expanded && !duplicates) {
      checkDuplicates();
    }
  }, [expanded]);

  const checkDuplicates = async () => {
    try {
      setLoading(true);
      const response = await apiService.findAccountDuplicates(accountId);
      setDuplicates(response.data);
    } catch (error) {
      console.error("Error checking duplicates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (duplicateId) => {
    try {
      setLoading(true);
      setSelectedDuplicate(duplicateId);
      const response = await apiService.previewAccountMerge(
        accountId,
        duplicateId,
      );
      setPreviewData(response.data);
    } catch (error) {
      console.error("Error previewing merge:", error);
      alert("Failed to preview merge");
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (
      !window.confirm(
        `Merge this account into "${previewData.target.name}"?\n\n` +
          `This will transfer ${previewData.totalRecords} records and cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setMerging(true);
      await apiService.mergeAccountInto(accountId, selectedDuplicate, {
        mergeFields: true,
      });
      alert("Accounts merged successfully!");
      window.location.href = `/accounts/${selectedDuplicate}`;
    } catch (error) {
      console.error("Error merging:", error);
      alert("Failed to merge accounts");
    } finally {
      setMerging(false);
    }
  };

  const totalDuplicates = duplicates?.totalDuplicates || 0;

  return (
    <div
      className={`rounded-lg border transition-colors ${
        isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full p-4 flex items-center justify-between transition-colors ${
          isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-2">
          <Users size={18} className="text-blue-400" />
          <span
            className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Possible Duplicates
          </span>
          {totalDuplicates > 0 && (
            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
              {totalDuplicates}
            </span>
          )}
        </div>
        <ArrowRight
          size={16}
          className={`transition-transform ${expanded ? "rotate-90" : ""} ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        />
      </button>

      {/* Content */}
      {expanded && (
        <div
          className={`p-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          {loading && !duplicates ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-blue-400" size={24} />
            </div>
          ) : !previewData ? (
            // Duplicates List
            <div className="space-y-3">
              {!duplicates || totalDuplicates === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle
                    size={32}
                    className="mx-auto mb-2 text-green-400 opacity-50"
                  />
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No duplicates found
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Found {totalDuplicates} potential duplicate
                      {totalDuplicates !== 1 ? "s" : ""}
                    </p>
                    <button
                      onClick={checkDuplicates}
                      className={`p-1 rounded transition-colors ${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                      title="Refresh"
                    >
                      <RefreshCw
                        size={14}
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      />
                    </button>
                  </div>

                  {duplicates.duplicateGroups.map((group, idx) => (
                    <div key={idx} className="space-y-2">
                      <div
                        className={`flex items-center gap-2 text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {group.reason === "phone" ? (
                          <>
                            <Phone size={12} /> Phone Match
                          </>
                        ) : (
                          <>
                            <Building2 size={12} /> Name Similar
                          </>
                        )}
                        <span className="ml-auto px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                          {(group.confidence * 100).toFixed(0)}%
                        </span>
                      </div>

                      {group.accounts.map((account) => (
                        <div
                          key={account.id}
                          className={`rounded-lg p-3 transition-colors ${
                            isDarkMode
                              ? "bg-gray-800/50 hover:bg-gray-800"
                              : "bg-gray-50 hover:bg-gray-100"
                          }`}
                        >
                          <div className="flex items-start gap-3 mb-2">
                            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {account.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-medium text-sm truncate ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {account.name}
                              </div>
                              {account.phone && (
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {account.phone}
                                </div>
                              )}
                              {account.billingCity && (
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {account.billingCity}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handlePreview(account.id)}
                            disabled={loading}
                            className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs font-medium transition-colors disabled:opacity-50 text-white"
                          >
                            Merge Into This
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            // Merge Preview
            <div className="space-y-4">
              <button
                onClick={() => {
                  setPreviewData(null);
                  setSelectedDuplicate(null);
                }}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
              >
                ← Back
              </button>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <div className="text-xs text-red-400 mb-1">Will Be Deleted</div>
                <div
                  className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {accountName}
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRight
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  size={20}
                />
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <div className="text-xs text-green-400 mb-1">Will Be Kept</div>
                <div
                  className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {previewData.target.name}
                </div>
              </div>

              <div
                className={`rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
              >
                <div
                  className={`text-xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Records to Transfer
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(previewData.recordsToMove).map(
                    ([key, count]) =>
                      count > 0 ? (
                        <div
                          key={key}
                          className={`rounded p-2 ${isDarkMode ? "bg-gray-700/50" : "bg-gray-200/60"}`}
                        >
                          <div
                            className={`font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {count}
                          </div>
                          <div
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </div>
                        </div>
                      ) : null,
                  )}
                </div>
                <div
                  className={`mt-2 pt-2 border-t flex justify-between ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <span
                    className={`font-medium text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    Total
                  </span>
                  <span
                    className={`font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {previewData.totalRecords}
                  </span>
                </div>
              </div>

              {previewData.warnings.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-yellow-400" />
                    <span className="text-xs font-medium text-yellow-400">
                      Warnings
                    </span>
                  </div>
                  {previewData.warnings.map((w, i) => (
                    <div
                      key={i}
                      className={`text-xs mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      • {w.message}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleMerge}
                disabled={merging}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-white"
              >
                {merging ? (
                  <Loader className="animate-spin" size={16} />
                ) : (
                  <>
                    <AlertTriangle size={16} />
                    Confirm Merge
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountDuplicatesSidebar;
